import { FC } from 'react';
import { Check, Lock } from 'lucide-react';
import { COURSE_STEPS } from '@new/features/courses/components/CourseCompletionSteps/StepsHeader/courseStepsConfig';
import { CourseStep } from '@new/features/courses/contexts/SingleCourseProvider';
import { CourseWithProgress } from '@teachur/http-client';

const SHORT_LABELS: Record<CourseStep, string> = {
  self: 'Self',
  exam: 'Exam',
  project: 'Project',
  hearing: 'Hearing',
};

interface StepIconProgressProps {
  course: CourseWithProgress;
}

export const StepIconProgress: FC<StepIconProgressProps> = ({ course }) => {
  const getProgress = (key: CourseStep) => {
    if (key === 'self') return `${course.selfAssessmentCompletionPercentage ?? 0}%`;
    if (key === 'exam') return course.quizCompleted ? '100%' : '0%';
    if (key === 'project') return course.projectCompleted ? '100%' : '0%';
    if (key === 'hearing') return course.coursePassed ? '100%' : '0%';
    return '0%';
  };

  return (
    <div className="flex flex-wrap justify-center md:justify-start gap-8">
      {COURSE_STEPS.map((step) => {
        const status = step.getStatus(course);
        const isIconStep = step.key === 'project' || step.key === 'hearing' || step.key === 'exam';
        const StepIcon = step.stepIcon;

        const config = {
          completed: {
            container: 'bg-(--success-S600) text-(--neutral-white-NW1000)',
            label: 'text-(--success-S600)',
            content: <Check className="w-3.5 h-3.5 stroke-3" />,
          },
          current: {
            container:
              'bg-(--primary-O1000) text-(--neutral-white-NW1000) font-semibold text-[10px] border-2 border-(--primary-O1000)',
            label: 'text-(--primary-O1000) font-semibold',
            content:
              isIconStep && StepIcon ? (
                <StepIcon className="w-3.5 h-3.5" />
              ) : (
                <span>{getProgress(step.key)}</span>
              ),
          },
          locked: {
            container:
              'bg-(--neutral-black-NB100) border border-(--neutral-black-NB200) text-(--neutral-black-NB400)',
            label: 'text-(--neutral-black-NB400)',
            content: <Lock className="w-3.5 h-3.5" />,
          },
        }[status];

        return (
          <div
            key={step.key}
            className={`flex flex-col gap-2 items-center cursor-pointer! ${status === 'locked' ? 'opacity-70' : ''}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${config.container}`}
            >
              {config.content}
            </div>

            <p className={`text-[10px]! font-semibold m-0! ${config.label}`}>
              {SHORT_LABELS[step.key]}
            </p>
          </div>
        );
      })}
    </div>
  );
};
